import React, { useEffect, useRef, useState } from "react";
import { AudioLines, Check, Loader2, Play, Plus, Square, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { rerecordAllBeats } from "./RerecordAllButton";
import { useDemoBrand } from "@/lib/demoBrand";

const errMsg = (e, fb) => e?.response?.data?.detail || fb;

export default function VoicePickerButton({ onRerecorded }) {
  const { slug } = useDemoBrand();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ active: null, voices: [] });
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [newId, setNewId] = useState("");
  const [newName, setNewName] = useState("");
  const [askRerecord, setAskRerecord] = useState(false);
  const [recording, setRecording] = useState(false);

  const [sampleBusy, setSampleBusy] = useState(null);
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);
  const stopSample = () => { try { audioRef.current?.pause(); } catch {} setPlaying(null); };
  useEffect(() => { if (!open) stopSample(); }, [open]);

  const playSample = async (voiceId) => {
    if (playing === voiceId) { stopSample(); return; }
    stopSample();
    setSampleBusy(voiceId);
    try {
      const { data: r } = await apiClient.post(`/admin/demos/${slug}/voices/${voiceId}/sample`);
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
      const a = new Audio(`${base}${r.url}`);
      audioRef.current = a;
      a.onended = () => setPlaying(null);
      setPlaying(voiceId);
      a.play().catch(() => setPlaying(null));
    } catch (e) { toast.error(errMsg(e, "Could not load sample")); }
    finally { setSampleBusy(null); }
  };

  const load = async () => {
    setLoading(true);
    try { setData((await apiClient.get(`/admin/demos/${slug}/voices`)).data); }
    catch (e) { toast.error(errMsg(e, "Could not load voices")); }
    finally { setLoading(false); }
  };

  const openPicker = () => { setOpen(true); load(); };

  const select = async (voiceId) => {
    if (voiceId === data.active) return;
    setBusyId(voiceId);
    try {
      await apiClient.put(`/admin/demos/${slug}/voices/active`, { voice_id: voiceId });
      setData((d) => ({ ...d, active: voiceId }));
      stopSample();
      setOpen(false);
      setAskRerecord(true);
    } catch (e) { toast.error(errMsg(e, "Could not switch voice")); }
    finally { setBusyId(null); }
  };

  const add = async () => {
    setBusyId("__add");
    try {
      const { data: v } = await apiClient.post(`/admin/demos/${slug}/voices`, { voice_id: newId, name: newName });
      setData((d) => ({ ...d, voices: [...d.voices, v] }));
      setNewId(""); setNewName("");
      toast.success(`Added ${v.name}`);
    } catch (e) { toast.error(errMsg(e, "Could not add voice")); }
    finally { setBusyId(null); }
  };

  const remove = async (voiceId) => {
    setBusyId(voiceId);
    try {
      await apiClient.delete(`/admin/demos/${slug}/voices/${voiceId}`);
      setData((d) => ({ ...d, voices: d.voices.filter((v) => v.voice_id !== voiceId) }));
    } catch (e) { toast.error(errMsg(e, "Could not remove voice")); }
    finally { setBusyId(null); }
  };

  const rerecord = async () => {
    setAskRerecord(false);
    setRecording(true);
    try {
      const r = await rerecordAllBeats(slug);
      toast.success(`Re-recorded ${r.done} beats in the new voice`);
      await onRerecorded?.();
    } catch (e) { toast.error(errMsg(e, "Re-record failed")); }
    finally { setRecording(false); }
  };

  const activeName = data.voices.find((v) => v.voice_id === data.active)?.name;

  return (
    <>
      <button
        type="button"
        onClick={openPicker}
        disabled={recording}
        data-testid="demo-voice-picker-open"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-brand-cyan/10 text-slate-300 hover:text-brand-cyan border border-white/10 hover:border-brand-cyan/40 font-mono text-[11px] font-semibold tracking-[0.14em] uppercase whitespace-nowrap transition-colors disabled:opacity-60"
      >
        {recording ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AudioLines className="w-3.5 h-3.5" />}
        {recording ? "Recording…" : "Voice"}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="demo-voice-picker-dialog" className="bg-[#0C1322] border-white/10 text-slate-100 max-w-md">
          <DialogHeader>
            <DialogTitle>Narration voice</DialogTitle>
            <DialogDescription className="text-slate-400">
              Pick the ElevenLabs voice used for the guided tour.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-brand-cyan" /></div>
          ) : (
            <ul className="space-y-2 max-h-[45vh] overflow-y-auto pr-1" data-testid="demo-voice-list">
              {data.voices.map((v) => {
                const active = v.voice_id === data.active;
                return (
                  <li
                    key={v.voice_id}
                    data-testid={`demo-voice-${v.voice_id}`}
                    className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors ${active ? "border-brand-cyan/60 bg-brand-cyan/10" : "border-white/10 hover:border-white/20 bg-white/[0.02]"}`}
                  >
                    <button
                      onClick={() => select(v.voice_id)}
                      disabled={!!busyId}
                      data-testid={`demo-voice-select-${v.voice_id}`}
                      className="flex-1 flex items-center gap-3 text-left"
                    >
                      <span className={`w-4 h-4 rounded-full border flex items-center justify-center ${active ? "border-brand-cyan bg-brand-cyan" : "border-slate-500"}`}>
                        {active && <Check className="w-3 h-3 text-navy-950" />}
                      </span>
                      <span>
                        <span className="block text-sm text-white">{v.name}</span>
                        <span className="block font-mono text-[10px] text-slate-500">{v.voice_id}</span>
                      </span>
                      {busyId === v.voice_id && <Loader2 className="w-3.5 h-3.5 animate-spin text-brand-cyan ml-auto" />}
                    </button>
                    <button
                      onClick={() => playSample(v.voice_id)}
                      disabled={sampleBusy === v.voice_id}
                      data-testid={`demo-voice-sample-${v.voice_id}`}
                      className={`p-1.5 rounded-md transition-colors ${playing === v.voice_id ? "text-emerald-300 bg-emerald-500/10" : "text-slate-400 hover:text-brand-cyan hover:bg-white/5"}`}
                      aria-label={playing === v.voice_id ? "Stop sample" : "Play sample"}
                      title="Hear a sample"
                    >
                      {sampleBusy === v.voice_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : playing === v.voice_id ? <Square className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                    </button>
                    {!active && (
                      <button
                        onClick={() => remove(v.voice_id)}
                        disabled={!!busyId}
                        data-testid={`demo-voice-remove-${v.voice_id}`}
                        className="p-1.5 rounded-md text-slate-500 hover:text-rose-300 hover:bg-rose-500/10 transition-colors"
                        aria-label="Remove voice"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          <div className="mt-2 pt-4 border-t border-white/10 space-y-2">
            <div className="font-mono text-[10px] tracking-[0.16em] uppercase text-slate-500">Add a voice</div>
            <input
              value={newId}
              onChange={(e) => setNewId(e.target.value)}
              placeholder="ElevenLabs voice ID"
              data-testid="demo-voice-add-id"
              className="w-full rounded-lg bg-navy-950/70 border border-white/10 focus:border-brand-cyan/60 outline-none px-3 py-2 text-sm font-mono"
            />
            <div className="flex gap-2">
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Label (optional)"
                data-testid="demo-voice-add-name"
                className="flex-1 rounded-lg bg-navy-950/70 border border-white/10 focus:border-brand-cyan/60 outline-none px-3 py-2 text-sm"
              />
              <button
                onClick={add}
                disabled={!!busyId || !newId.trim()}
                data-testid="demo-voice-add-submit"
                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-brand-cyan/90 hover:bg-brand-cyan text-navy-950 font-mono text-[10px] font-semibold tracking-[0.12em] uppercase transition-colors disabled:opacity-40"
              >
                {busyId === "__add" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Add
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={askRerecord} onOpenChange={setAskRerecord}>
        <AlertDialogContent data-testid="demo-voice-rerecord-dialog" className="bg-[#0C1322] border-white/10 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle>Re-record all beats now?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {activeName || "The new voice"} is saved. Re-record every beat now so the whole tour uses it, or wait and beats switch as they're edited. Uses ElevenLabs credits, about 30–60 seconds.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="demo-voice-rerecord-later">Later</AlertDialogCancel>
            <AlertDialogAction data-testid="demo-voice-rerecord-now" onClick={rerecord}>Re-record now</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
